import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, router, usePage } from '@inertiajs/react';
import { format } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Calendar, Clock, Save } from 'lucide-react';

const sessionFormSchema = z.object({
  session_date: z.string().min(1, 'Session date is required'),
  duration_minutes: z.string().min(1, 'Duration is required'),
  occurred: z.boolean(),
  discussion_summary: z.string().min(1, 'Discussion summary is required'),
  mentee_todos: z.string().optional(),
  next_session_date: z.string().optional(),
});

type SessionFormValues = z.infer<typeof sessionFormSchema>;

interface Match {
  id: number;
  mentor: {
    id: number;
    first_name: string;
    last_name: string;
  };
  mentee: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

interface Props {
  match: Match;
  session?: {
    session_date: string;
    duration_minutes: number;
    occurred: boolean;
  };
}

export default function MentorshipSessionsNew({ match, session }: Props) {
  const form = useForm<SessionFormValues>({
    resolver: zodResolver(sessionFormSchema),
    defaultValues: {
      session_date: format(new Date(), 'yyyy-MM-dd'),
      duration_minutes: '30',
      occurred: true,
      discussion_summary: '',
      mentee_todos: '',
      next_session_date: '',
    },
  });

  const onSubmit = (data: SessionFormValues) => {
    router.post(`/matches/${match.id}/mentorship_sessions`, {
      mentorship_session: {
        ...data,
        duration_minutes: parseInt(data.duration_minutes),
      },
    });
  };

  const durationOptions = [
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" asChild>
              <Link href={`/matches/${match.id}/mentorship_sessions`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sessions
              </Link>
            </Button>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Log Mentorship Session
          </h1>
          <p className="text-gray-600">
            Record your session with {match.mentee.first_name}{' '}
            {match.mentee.last_name}
          </p>
        </div>

        <Card className="border-0 shadow-sm pt-0 pb-6">
          <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
            <CardTitle className="text-lg font-medium">Session Details</CardTitle>
            <CardDescription className="text-sm text-gray-500 mt-1">
              Fill in the details about your mentorship session
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="session_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Session Date
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          max={format(new Date(), 'yyyy-MM-dd')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="duration_minutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Duration
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {durationOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="occurred"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Session occurred</FormLabel>
                      <FormDescription>
                        Uncheck if the session was cancelled or the mentee
                        didn't show up
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discussion_summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What did you discuss?</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Summarize the topics covered during the session..."
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mentee_todos"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>TODOs for Mentee (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="List any action items or tasks for the mentee to complete before the next session..."
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      These will be visible to the mentee
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="next_session_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Next Session Date (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        min={format(new Date(), 'yyyy-MM-dd')}
                      />
                    </FormControl>
                    <FormDescription>
                      When do you plan to meet next?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  Save Session
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link href={`/matches/${match.id}/mentorship_sessions`}>
                    Cancel
                  </Link>
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  </div>
  );
}
